import type { IndexUiState as SearchState } from "instantsearch.js";

type AnalyticsTrackingValue = {
  filterKey: string;
  value: string | boolean;
};

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
const difference = (
  arr1: string | string[],
  arr2: string | string[]
): string[] => {
  if (!Array.isArray(arr1)) arr1 = [arr1];
  if (!Array.isArray(arr2)) arr2 = [arr2];

  //console.debug(arr1, arr2);
  const result = (arr1 || []).filter((x) => !(arr2 || []).includes(x));
  return result;
};

const mapDepartureDateFilterValue = (value: string): string => {
  const yearMonth = value.split("-");
  if (yearMonth.length !== 2) return value;
  const year = yearMonth[0];
  const month = ("00" + yearMonth[1]).slice(-2);
  return `${year}-${month}`;
};

const mapDurationFilterValue = (value: string): string => {
  switch (value) {
    case "1-8":
      return "1";
    case "9-12":
      return "9";
    case "13-18":
      return "13";
    case "19+":
      return "19";
  }
  return value;
};

const mapShipFilterValue = (value: string): string => {
  switch (value) {
    case "Silver Cloud":
      return "9";
    case "Silver Explorer":
      return "2";
    case "Silver Wind":
      return "7";
  }
  return value;
};

const mapSortByValue = (value: string): string => {
  switch (value) {
    case "prod_cruises":
      return "departure_asc";
    case "prod_cruises-price-from-usd-asc":
      return "price_asc";
    case "prod_cruises-price-from-usd-desc":
      return "price_desc";
    case "prod_cruises-duration-asc":
      return "duration_asc";
    case "prod_cruises-duration-desc":
      return "duration_desc";
  }
  return value;
};

const transformToOldFormat = (filterKey: string, value: string | boolean) => {
  const stringValue = value?.toString();
  switch (filterKey) {
    case "departureYearMonth":
      return `filters_a_departure_${mapDepartureDateFilterValue(stringValue)}`;
    case "dayGroup":
      return `filters_a_duration_${mapDurationFilterValue(stringValue)}`;
    case "content.shipName":
      return `filters_a_ships_${mapShipFilterValue(stringValue)}`;
    case "sortBy":
      return `filters_a_sort_${mapSortByValue(stringValue)}`;
    case "content.specialType":
      return `filters_a_specialType_${stringValue}`;
    case "available":
      return `filters_a_availableOnly_${stringValue}`;
    case "query":
      return `filters_a_query_${stringValue}`;
  }
  return `filters_untransformed_${filterKey}_${stringValue}`;
};

const getRefinementListAdditions = (
  searchState: SearchState,
  prevSearchState: SearchState
): AnalyticsTrackingValue[] => {
  const result = Object.entries(searchState.refinementList || {})
    .map(([key, values]): [string, string[]] => {
      return [
        key,
        difference(
          values,
          prevSearchState && prevSearchState.refinementList
            ? prevSearchState.refinementList[key]
            : []
        )
      ];
    })
    .filter(([_, values]) => values?.length)
    .flatMap(([filterKey, values]) =>
      values.map((value) => ({ filterKey, value }))
    );

  return result;
};

const getToggleAdditions = (
  searchState: SearchState,
  prevSearchState: SearchState
): AnalyticsTrackingValue[] => {
  const result = Object.entries(searchState.toggle || {})
    .map(([filterKey, value]) => ({
      filterKey,
      value:
        !prevSearchState ||
        !prevSearchState.toggle ||
        prevSearchState.toggle[filterKey] !== value
          ? value
          : null
    }))
    // only "on" values
    .filter((x): x is { filterKey: string; value: true } => x.value == true);
  return result;
};

const getSimpleValueChanges = (
  searchState: SearchState,
  prevSearchState: SearchState,
  key: string
): AnalyticsTrackingValue[] =>
  (searchState[key] !== (prevSearchState || {})[key]
    ? [searchState[key]]
    : []
  ).map((value) => ({ filterKey: key, value }));

type MapFilterStateChangeToLegacyEventValuesReturnItem = {
  filterKey: string;
  value: string;
};

export const mapFilterStateChangeToLegacyEventValues = (
  searchState: SearchState,
  prevSearchState: SearchState
): MapFilterStateChangeToLegacyEventValuesReturnItem[] => {
  const refinementListAdditions = getRefinementListAdditions(
    searchState,
    prevSearchState
  );

  const toggleAdditions = getToggleAdditions(searchState, prevSearchState);

  const sortByChanges = getSimpleValueChanges(
    searchState,
    prevSearchState,
    "sortBy"
  );

  const queryChanges = getSimpleValueChanges(
    searchState,
    prevSearchState,
    "query"
  );

  return refinementListAdditions
    .concat(toggleAdditions)
    .concat(sortByChanges)
    .concat(queryChanges)
    .map(({ filterKey, value }) => ({
      filterKey,
      value: transformToOldFormat(filterKey, value)
    }));
};
